import React, { useCallback, useState } from 'react';

import { JobEntity, JobOperation } from '@commenda/api';
import {
  Button,
  Dropdown,
  HorizontalTabsList,
  Icons,
  LoadingIndicator,
  Tabs,
  TabsTrigger,
  useToggle,
} from '@commenda/kakashi';

import {
  ImportTransactionsChoiceModal,
  IndirectTaxDataSyncBanner,
  IndirectTaxJobsLogDialog,
  IndirectTaxRegion,
  SalesTaxImportTransactionsModal,
} from '../../common';
import {
  IndirectTaxBulkDeleteTransactionsModal,
  IndirectTaxTransactionActionModal,
  IndirectTaxTransactionsEmptyState,
  IndirectTaxTransactionsTable,
} from '../components';
import { useNotification } from '../../../hooks/useNotification';
import { IndirectTaxTransactionHelper } from '../helpers';
import {
  TIndirectTaxTransaction,
  TIndirectTaxTransactionStatus,
} from '../types';

import {
  IndirectTaxTransactionsDashboardInitialState,
  useIndirectTaxTransactionsDashboard,
} from './useIndirectTaxTransactionsDashboard';

interface IndirectTaxTransactionsDashboardProps {
  header: React.ReactNode;
  corporationId: number;
  companyId: number;
  onTransactionClick?: (transaction: TIndirectTaxTransaction) => void;
  onCustomerClick?: (customerId: string) => void;
  initialState?: IndirectTaxTransactionsDashboardInitialState;
  redirectToTaxCalculator?: () => void
}

const IndirectTaxTransactionsDashboard = ({
  header,
  corporationId,
  companyId,
  onTransactionClick,
  onCustomerClick,
  initialState,
  redirectToTaxCalculator
}: IndirectTaxTransactionsDashboardProps) => {
  const {
    activeRegion,
    transactions,
    transactionsData,
    summaryConfigs,
    hasTransactions,
    aggregates,
    activeFilters,
    filterOptionsData,
    searchInput,
    cursorHistory,
    handleNextPage,
    handlePreviousPage,
    isLoadingTransactions,
    isInitialLoading,
    transactionsError,
    isTransactionJobInProgress,
    isIntegrationSyncInProgress,
    handleRegionChange,
    handleFilterChange,
    handleSearchChange,
    handleConnectIntegration,
    connectRootFiIntegrationModal,
    importTransactionsModal,
    getJobsByEntityAndOperation,
    mutateTransactions,
    refreshJobs,
    refreshCsvJobs,
    transactionSelection,
    bulkDeleteModal,
    handleRowAction,
    handleBulkDelete,
    handleBulkExport,
  } = useIndirectTaxTransactionsDashboard({ corporationId, initialState });

  const importChoiceModal = useToggle();
  const importJobTableVisibility = useToggle();
  const exportJobTableVisibility = useToggle();

  const [actionModalTransaction, setActionModalTransaction] =
    useState<TIndirectTaxTransaction | null>(null);
  const [actionModalAction, setActionModalAction] =
    useState<TIndirectTaxTransactionStatus | null>(null);

  const [isRetrying, setIsRetrying] = useState(false);
  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    try {
      // Hard refresh: drop the cached error and force a refetch.
      await mutateTransactions?.(undefined, { revalidate: true });
    } finally {
      setIsRetrying(false);
    }
  }, [mutateTransactions]);

  const { setNotification } = useNotification();

  const onRowActionClick = useCallback(
    (
      transaction: TIndirectTaxTransaction,
      action: TIndirectTaxTransactionStatus
    ) => {
      setActionModalTransaction(transaction);
      setActionModalAction(action);
    },
    []
  );

  const onActionConfirm = useCallback(async () => {
    if (!actionModalTransaction || !actionModalAction) return;
    const result = await handleRowAction(
      actionModalTransaction,
      actionModalAction
    );
    if (result.success) {
      const actionName =
        actionModalAction === TIndirectTaxTransactionStatus.VOIDED
          ? 'voided'
          : actionModalAction === TIndirectTaxTransactionStatus.COMMITTED
            ? 'committed'
            : 'locked';
      setNotification({
        show: true,
        valid: true,
        title: `Transaction ${actionName}`,
        message: '',
      });
    } else {
      setNotification({
        show: true,
        valid: false,
        title: 'Action failed',
        message: result.error || 'Failed to update transaction',
      });
    }
    setActionModalTransaction(null);
    setActionModalAction(null);
  }, [
    actionModalTransaction,
    actionModalAction,
    handleRowAction,
    setNotification,
  ]);

  const onBulkDelete = useCallback(async () => {
    const result = await handleBulkDelete();
    if (result.success) {
      setNotification({
        show: true,
        valid: true,
        title: 'Transactions deleted successfully',
        message: '',
      });
    } else {
      setNotification({
        show: true,
        valid: false,
        title: 'Delete failed',
        message: result.error || 'Failed to delete transactions',
      });
    }
    bulkDeleteModal.hide();
  }, [handleBulkDelete, setNotification, bulkDeleteModal]);

  const onBulkExport = useCallback(async () => {
    const result = await handleBulkExport();
    if (result.success) {
      setNotification({
        show: true,
        valid: true,
        title: 'Export in progress',
        message: 'You will receive an email.',
      });
    } else {
      setNotification({
        show: true,
        valid: false,
        title: 'Export failed',
        message: result.error || 'Failed to export transactions',
      });
    }
  }, [handleBulkExport, setNotification]);

  if (isInitialLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between p-3xl">
          <div className="flex-1">{header}</div>
        </div>
        <div className="flex items-center justify-center py-9xl">
          <LoadingIndicator />
        </div>
      </div>
    );
  }

  if (transactionsError) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between p-3xl">
          <div className="flex-1">{header}</div>
        </div>
        <div className="flex flex-col items-center justify-center py-9xl gap-md">
          <p className="text-sm text-text-secondary">
            Failed to load transactions. Please try again.
          </p>
          <Button
            variant="secondary"
            size="sm"
            isLoading={isRetrying}
            onClick={handleRetry}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-3xl pb-0">
        <div className="flex-1">{header}</div>

        <div className="flex items-center gap-md">
          <Dropdown
            menu={{
              groups: [
                {
                  items: [
                    {
                      icon: Icons.Download01,
                      label: 'View import logs',
                      onClick: importJobTableVisibility.show,
                    },
                    {
                      icon: Icons.Upload01,
                      label: 'View export logs',
                      onClick: exportJobTableVisibility.show,
                    },
                  ],
                },
              ],
            }}
            align="end"
          >
            <Button variant="tertiary" size="sm">
              <Icons.DotsVertical className="size-4" />
            </Button>
          </Dropdown>

          {redirectToTaxCalculator && <Button variant="secondary" size="sm" onClick={redirectToTaxCalculator}>
            <Icons.Percent01 className="size-4" />
            <span>Use tax calculator</span>
          </Button>}

          <Dropdown
            align="end"
            menu={{
              groups: [
                {
                  items: [
                    {
                      icon: Icons.Link01,
                      label: 'Connect integration',
                      onClick: handleConnectIntegration,
                    },
                    {
                      icon: Icons.Download01,
                      label: 'Import transactions',
                      onClick: importChoiceModal.show,
                    },
                  ],
                },
              ],
            }}
          >
            <Button variant="primary" size="sm">
              <Icons.Plus className="size-4" />
              <span>Add transaction</span>
            </Button>
          </Dropdown>
        </div>
      </div>

      <div
        className="border-b border-border-primary px-3xl pt-3xl"
        data-ai-section="Region tabs"
      >
        <Tabs
          value={activeRegion}
          onValueChange={(value) =>
            handleRegionChange(value as IndirectTaxRegion)
          }
        >
          <HorizontalTabsList variant="underline" className="border-b-0 gap-lg">
            {IndirectTaxTransactionHelper.REGION_TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </HorizontalTabsList>
        </Tabs>
      </div>

      {(isTransactionJobInProgress || isIntegrationSyncInProgress) && (
        <IndirectTaxDataSyncBanner
          corporationId={corporationId}
          entity={JobEntity.INVOICE}
          isSyncInProgress={isIntegrationSyncInProgress}
        />
      )}

      {hasTransactions ? (
        <div className="p-3xl" data-ai-section="Transactions table">
          <IndirectTaxTransactionsTable
            transactions={transactions}
            activeRegion={activeRegion}
            activeFilters={activeFilters}
            filterOptionsData={filterOptionsData}
            onFilterChange={handleFilterChange}
            summaryConfigs={summaryConfigs}
            aggregates={aggregates}
            onRowClick={onTransactionClick}
            searchValue={searchInput}
            onSearchChange={handleSearchChange}
            paginationConfig={{
              hasNextPage: !!transactionsData.nextCursor,
              hasPreviousPage: cursorHistory.length > 0,
              onNextPage: handleNextPage,
              onPreviousPage: handlePreviousPage,
              totalItems: transactionsData.totalTransactions,
              currentPage: cursorHistory.length,
            }}
            onRowAction={onRowActionClick}
            transactionSelection={transactionSelection}
            totalTransactions={transactionsData.totalTransactions}
            onBulkExport={onBulkExport}
            onBulkDelete={undefined}
            onCustomerClick={onCustomerClick}
          />
        </div>
      ) : (
        <IndirectTaxTransactionsEmptyState
          onConnectIntegration={handleConnectIntegration}
          onImportTransactions={importChoiceModal.show}
        />
      )}

      <IndirectTaxJobsLogDialog
        isOpen={importJobTableVisibility.visible}
        onClose={importJobTableVisibility.hide}
        jobs={getJobsByEntityAndOperation(
          JobEntity.INVOICE,
          JobOperation.IMPORT
        )}
        corporationId={corporationId}
        jobOperation={JobOperation.IMPORT}
        title="Transaction reports"
      />

      <IndirectTaxJobsLogDialog
        isOpen={exportJobTableVisibility.visible}
        onClose={exportJobTableVisibility.hide}
        jobs={getJobsByEntityAndOperation(
          JobEntity.INVOICE,
          JobOperation.EXPORT
        )}
        corporationId={corporationId}
        jobOperation={JobOperation.EXPORT}
        title="Transaction reports"
      />

      {actionModalTransaction && (
        <IndirectTaxTransactionActionModal
          isOpen={!!actionModalTransaction}
          onClose={() => {
            setActionModalTransaction(null);
            setActionModalAction(null);
          }}
          transaction={actionModalTransaction}
          action={actionModalAction}
          onConfirm={onActionConfirm}
        />
      )}

      <IndirectTaxBulkDeleteTransactionsModal
        isOpen={bulkDeleteModal.visible}
        onClose={bulkDeleteModal.hide}
        onDelete={onBulkDelete}
        totalSelected={transactionSelection.totalSelected}
      />

      <ImportTransactionsChoiceModal
        open={importChoiceModal.visible}
        onOpenChange={(v) =>
          v ? importChoiceModal.show() : importChoiceModal.hide()
        }
        companyId={companyId}
        corporationId={corporationId}
        setNotification={setNotification}
        onSelectImportMyself={() => {
          importChoiceModal.hide();
          importTransactionsModal.show();
        }}
      />

      <SalesTaxImportTransactionsModal
        isTransactionsModalShown={importTransactionsModal.visible}
        setIsTransactionsModalShown={(shown: boolean) =>
          shown
            ? importTransactionsModal.show()
            : importTransactionsModal.hide()
        }
        corporationId={corporationId}
        setNotification={setNotification as never}
        onSuccess={async () => {
          refreshJobs();
          refreshCsvJobs();
          mutateTransactions();
        }}
        companyId={companyId}
        copyTransactionDateToReportingDate
      />

      {connectRootFiIntegrationModal}
    </div>
  );
};

export { IndirectTaxTransactionsDashboard };
